import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { endpointLink } from '../../index';
import { useApiAccessToken } from '../../hooks/useApiAccessToken';

type ActivationMode = 'OTAA' | 'ABP';
type RegistrationMode = 'single' | 'bulk';

const FREQUENCY_PLANS = [
  { value: 'US_902_928_FSB_2', label: 'United States 902-928 MHz, FSB 2 (used by TTN)' },
  { value: 'US_902_928_FSB_1', label: 'United States 902-928 MHz, FSB 1' },
  { value: 'US_902_928_FSB_8', label: 'United States 902-928 MHz, FSB 8' },
];

const LORAWAN_VERSIONS = [
  { value: 'MAC_V1_0_4', label: 'LoRaWAN Specification 1.0.4' },
  { value: 'MAC_V1_0_3', label: 'LoRaWAN Specification 1.0.3' },
  { value: 'MAC_V1_0_2', label: 'LoRaWAN Specification 1.0.2' },
  { value: 'MAC_V1_0_1', label: 'LoRaWAN Specification 1.0.1' },
  { value: 'MAC_V1_0', label: 'LoRaWAN Specification 1.0.0' },
  { value: 'MAC_V1_1', label: 'LoRaWAN Specification 1.1.0' },
];

const REGIONAL_PARAMETER_VERSIONS = [
  { value: 'RP002_V1_0_4', label: 'RP002 Regional Parameters 1.0.4' },
  { value: 'RP002_V1_0_3', label: 'RP002 Regional Parameters 1.0.3' },
  { value: 'RP002_V1_0_2', label: 'RP002 Regional Parameters 1.0.2' },
  { value: 'RP002_V1_0_1', label: 'RP002 Regional Parameters 1.0.1' },
  { value: 'RP002_V1_0_0', label: 'RP002 Regional Parameters 1.0.0' },
  { value: 'RP001_V1_0_3_REV_A', label: 'RP001 Regional Parameters 1.0.3 revision A' },
  { value: 'RP001_V1_0_2_REV_B', label: 'RP001 Regional Parameters 1.0.2 revision B' },
  { value: 'RP001_V1_0_2', label: 'RP001 Regional Parameters 1.0.2 revision A' },
  { value: 'RP001_V1_1_REV_B', label: 'RP001 Regional Parameters 1.1 revision B' },
  { value: 'RP001_V1_1_REV_A', label: 'RP001 Regional Parameters 1.1 revision A' },
];

const isHex = (value: string) => /^[a-fA-F0-9]+$/.test(value);

const pickField = (obj: any, keys: string[]): string | null => {
  for (const key of keys) {
    if (obj && obj[key] !== undefined && obj[key] !== null) {
      return String(obj[key]);
    }
  }
  return null;
};

type ParsedDevice = {
  device_id: string;
  dev_eui: string;
  join_eui: string;
  app_key: string;
  issues: string[];
};

const parseDevicesFromJson = (text: string): { devices: ParsedDevice[]; error: string } => {
  let parsed: any;
  try {
    parsed = JSON.parse(text);
  } catch (e) {
    return { devices: [], error: `Invalid JSON: ${(e as Error).message}` };
  }
  if (!Array.isArray(parsed)) {
    return { devices: [], error: 'JSON root must be an array of device entries.' };
  }

  const devices: ParsedDevice[] = parsed.map((entry: any) => {
    const deviceId = (pickField(entry, [
      'device_id',
      'deviceID',
      'DeviceID',
      'device-id',
      'name',
      'Name',
    ]) || '').trim();

    const explicitDevEui = pickField(entry, ['dev_eui', 'DevEUI', 'devEUI']);
    const uid = pickField(entry, ['UID', 'uid']);
    const derivedFromUid = uid ? uid.split(',').pop() || '' : '';
    const devEui = (explicitDevEui || derivedFromUid).trim().toUpperCase();

    const joinEui = (pickField(entry, ['join_eui', 'JoinEUI', 'JoinID', 'joinEUI']) || '').trim().toUpperCase();
    const appKey = (pickField(entry, ['app_key', 'AppKey', 'appKey']) || '').trim().toUpperCase();

    const issues: string[] = [];
    if (!deviceId) issues.push('missing device_id');
    if (!devEui || devEui.length !== 16 || !isHex(devEui)) issues.push('dev_eui must be 16 hex chars');
    if (!joinEui || joinEui.length !== 16 || !isHex(joinEui)) issues.push('join_eui must be 16 hex chars');
    if (!appKey || appKey.length !== 32 || !isHex(appKey)) issues.push('app_key must be 32 hex chars');

    return { device_id: deviceId, dev_eui: devEui, join_eui: joinEui, app_key: appKey, issues };
  });

  return { devices, error: '' };
};

type BulkSuccess = { device_id: string; dev_eui: string };
type BulkFailure = {
  device_id: string;
  dev_eui?: string;
  stage?: string;
  error: string;
  details?: any;
};

const TtnSensorRegistrationGrid: React.FC = () => {
  const getApiAccessToken = useApiAccessToken({ returnTo: '/sensors/register' });

  const [mode, setMode] = useState<RegistrationMode>('single');

  // Shared LoRaWAN settings
  const [applicationId, setApplicationId] = useState('');
  const [activationMode, setActivationMode] = useState<ActivationMode>('OTAA');
  const [frequencyPlanId, setFrequencyPlanId] = useState(FREQUENCY_PLANS[0].value);
  const [lorawanVersion, setLorawanVersion] = useState(LORAWAN_VERSIONS[0].value);
  const [regionalParametersVersion, setRegionalParametersVersion] = useState(
    REGIONAL_PARAMETER_VERSIONS[0].value,
  );

  // Single-device state
  const [deviceId, setDeviceId] = useState('');
  const [devEui, setDevEui] = useState('');
  const [joinEui, setJoinEui] = useState('');
  const [appKey, setAppKey] = useState('');
  const [nwkKey, setNwkKey] = useState('');
  const [devAddr, setDevAddr] = useState('');
  const [appSKey, setAppSKey] = useState('');
  const [nwkSKey, setNwkSKey] = useState('');

  // Bulk-upload state
  const [bulkFileName, setBulkFileName] = useState('');
  const [bulkParseError, setBulkParseError] = useState('');
  const [parsedDevices, setParsedDevices] = useState<ParsedDevice[]>([]);
  const [bulkSuccesses, setBulkSuccesses] = useState<BulkSuccess[]>([]);
  const [bulkFailures, setBulkFailures] = useState<BulkFailure[]>([]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const singleValidationError = useMemo(() => {
    if (!applicationId.trim()) return 'Application ID is required.';
    if (!deviceId.trim()) return 'Device ID is required.';
    if (!devEui.trim() || devEui.length !== 16 || !isHex(devEui)) return 'DevEUI must be a 16-character hex value.';
    if (!joinEui.trim() || joinEui.length !== 16 || !isHex(joinEui)) return 'JoinEUI must be a 16-character hex value.';

    if (activationMode === 'OTAA') {
      if (!appKey.trim() || appKey.length !== 32 || !isHex(appKey)) {
        return 'AppKey must be a 32-character hex value for OTAA.';
      }
      if (nwkKey.trim() && (nwkKey.length !== 32 || !isHex(nwkKey))) {
        return 'NwkKey must be a 32-character hex value when provided.';
      }
    }

    if (activationMode === 'ABP') {
      if (!devAddr.trim() || devAddr.length !== 8 || !isHex(devAddr)) {
        return 'DevAddr must be an 8-character hex value for ABP.';
      }
      if (!appSKey.trim() || appSKey.length !== 32 || !isHex(appSKey)) {
        return 'AppSKey must be a 32-character hex value for ABP.';
      }
      if (!nwkSKey.trim() || nwkSKey.length !== 32 || !isHex(nwkSKey)) {
        return 'NwkSKey must be a 32-character hex value for ABP.';
      }
    }

    return '';
  }, [activationMode, appKey, appSKey, applicationId, devAddr, devEui, deviceId, joinEui, nwkKey, nwkSKey]);

  const bulkValidationError = useMemo(() => {
    if (mode !== 'bulk') return '';
    if (!applicationId.trim()) return 'Application ID is required.';
    if (activationMode !== 'OTAA') return 'Bulk upload currently supports OTAA devices only.';
    if (parsedDevices.length === 0) return 'Upload a JSON file with at least one device.';
    const invalid = parsedDevices.filter((d) => d.issues.length > 0);
    if (invalid.length > 0) return `${invalid.length} of ${parsedDevices.length} devices have issues. Fix or remove them and re-upload.`;
    return '';
  }, [activationMode, applicationId, mode, parsedDevices]);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    setBulkSuccesses([]);
    setBulkFailures([]);
    if (!file) return;
    setBulkFileName(file.name);
    try {
      const text = await file.text();
      const { devices, error } = parseDevicesFromJson(text);
      setParsedDevices(devices);
      setBulkParseError(error);
    } catch (err) {
      setParsedDevices([]);
      setBulkParseError(err instanceof Error ? err.message : 'Failed to read file.');
    }
  };

  const submitSingle = async () => {
    const token = await getApiAccessToken();

    const payload = {
      application_id: applicationId.trim(),
      activation_mode: activationMode,
      end_device: {
        ids: {
          device_id: deviceId.trim(),
          application_ids: { application_id: applicationId.trim() },
          dev_eui: devEui.trim().toUpperCase(),
          join_eui: joinEui.trim().toUpperCase(),
        },
        frequency_plan_id: frequencyPlanId,
        lorawan_version: lorawanVersion,
        lorawan_phy_version: regionalParametersVersion,
        supports_join: activationMode === 'OTAA',
      },
      root_keys:
        activationMode === 'OTAA'
          ? {
              app_key: { key: appKey.trim().toUpperCase() },
              ...(nwkKey.trim() ? { nwk_key: { key: nwkKey.trim().toUpperCase() } } : {}),
            }
          : undefined,
      session:
        activationMode === 'ABP'
          ? {
              dev_addr: devAddr.trim().toUpperCase(),
              keys: {
                app_s_key: { key: appSKey.trim().toUpperCase() },
                nwk_s_key: { key: nwkSKey.trim().toUpperCase() },
              },
            }
          : undefined,
    };

    const response = await fetch(`${endpointLink}ttn/devices/register`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const responseText = await response.text();
    if (!response.ok) {
      throw new Error(responseText || `Registration failed with status ${response.status}`);
    }

    const parsedResponse = responseText ? JSON.parse(responseText) : {};
    const createdDeviceId = parsedResponse?.device_id || deviceId;
    setSuccessMessage(`Sensor ${createdDeviceId} was registered with TTN.`);
  };

  const submitBulk = async () => {
    const token = await getApiAccessToken();

    const payload = {
      application_id: applicationId.trim(),
      activation_mode: activationMode,
      frequency_plan_id: frequencyPlanId,
      lorawan_version: lorawanVersion,
      lorawan_phy_version: regionalParametersVersion,
      devices: parsedDevices.map((d) => ({
        device_id: d.device_id,
        dev_eui: d.dev_eui,
        join_eui: d.join_eui,
        app_key: d.app_key,
      })),
    };

    const response = await fetch(`${endpointLink}ttn/devices/register-bulk`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const responseText = await response.text();
    let parsed: any = {};
    try {
      parsed = responseText ? JSON.parse(responseText) : {};
    } catch {
      parsed = { message: responseText };
    }

    if (response.status >= 500 || !parsed) {
      throw new Error(parsed?.error || `Bulk registration failed with status ${response.status}`);
    }

    const successes: BulkSuccess[] = Array.isArray(parsed.successful) ? parsed.successful : [];
    const failures: BulkFailure[] = Array.isArray(parsed.failed) ? parsed.failed : [];
    setBulkSuccesses(successes);
    setBulkFailures(failures);

    if (failures.length === 0 && successes.length > 0) {
      setSuccessMessage(`Registered ${successes.length} device${successes.length === 1 ? '' : 's'} with TTN.`);
    } else if (successes.length > 0 && failures.length > 0) {
      setErrorMessage(`Registered ${successes.length} device${successes.length === 1 ? '' : 's'}; ${failures.length} failed. See results below.`);
    } else {
      setErrorMessage(`No devices were registered. See results below.`);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const validationError = mode === 'single' ? singleValidationError : bulkValidationError;
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    setIsSubmitting(true);
    setErrorMessage('');
    setSuccessMessage('');

    try {
      if (mode === 'single') {
        await submitSingle();
      } else {
        await submitBulk();
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown registration error.';
      setErrorMessage(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const validationError = mode === 'single' ? singleValidationError : bulkValidationError;
  const submitLabel = mode === 'single'
    ? (isSubmitting ? 'Registering...' : 'Register Sensor')
    : (isSubmitting ? `Registering ${parsedDevices.length} devices...` : `Register ${parsedDevices.length || ''} device${parsedDevices.length === 1 ? '' : 's'}`.trim());

  return (
    <Box sx={{ p: 3, width: '100%' }}>
      <Paper sx={{ p: 3, maxWidth: 1000, mx: 'auto' }}>
        <Typography variant="h4" sx={{ mb: 1 }}>
          Register TTN Sensor
        </Typography>
        <Typography variant="body2" sx={{ mb: 3 }}>
          Set the application and LoRaWAN settings once, then register a single device or upload a JSON file to onboard a batch.
        </Typography>

        <Stack component="form" onSubmit={handleSubmit} spacing={2}>
          <ToggleButtonGroup
            value={mode}
            exclusive
            onChange={(_event, next) => {
              if (next) setMode(next as RegistrationMode);
            }}
            size="small"
          >
            <ToggleButton value="single">Single device</ToggleButton>
            <ToggleButton value="bulk">Bulk upload</ToggleButton>
          </ToggleButtonGroup>

          <TextField
            required
            label="Application ID"
            value={applicationId}
            onChange={(event) => setApplicationId(event.target.value)}
            helperText="TTN application that owns these sensors"
          />

          <FormControl fullWidth>
            <InputLabel id="activation-mode-label">Activation mode</InputLabel>
            <Select
              labelId="activation-mode-label"
              value={activationMode}
              label="Activation mode"
              onChange={(event) => setActivationMode(event.target.value as ActivationMode)}
            >
              <MenuItem value="OTAA">OTAA</MenuItem>
              <MenuItem value="ABP">ABP</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel id="frequency-plan-label">Frequency plan</InputLabel>
            <Select
              labelId="frequency-plan-label"
              value={frequencyPlanId}
              label="Frequency plan"
              onChange={(event) => setFrequencyPlanId(event.target.value)}
            >
              {FREQUENCY_PLANS.map((plan) => (
                <MenuItem key={plan.value} value={plan.value}>
                  {plan.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <FormControl fullWidth>
              <InputLabel id="lorawan-version-label">LoRaWAN version</InputLabel>
              <Select
                labelId="lorawan-version-label"
                value={lorawanVersion}
                label="LoRaWAN version"
                onChange={(event) => setLorawanVersion(event.target.value)}
              >
                {LORAWAN_VERSIONS.map((version) => (
                  <MenuItem key={version.value} value={version.value}>
                    {version.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel id="regional-params-label">Regional Parameters version</InputLabel>
              <Select
                labelId="regional-params-label"
                value={regionalParametersVersion}
                label="Regional Parameters version"
                onChange={(event) => setRegionalParametersVersion(event.target.value)}
              >
                {REGIONAL_PARAMETER_VERSIONS.map((version) => (
                  <MenuItem key={version.value} value={version.value}>
                    {version.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          <Divider sx={{ my: 1 }} />

          {mode === 'single' ? (
            <>
              <TextField
                required
                label="Device ID"
                value={deviceId}
                onChange={(event) => setDeviceId(event.target.value)}
                helperText="Lowercase, unique within the TTN application"
              />

              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  required
                  label="DevEUI"
                  value={devEui}
                  onChange={(event) => setDevEui(event.target.value)}
                  helperText="16 hex characters"
                  fullWidth
                />
                <TextField
                  required
                  label="JoinEUI"
                  value={joinEui}
                  onChange={(event) => setJoinEui(event.target.value)}
                  helperText="16 hex characters"
                  fullWidth
                />
              </Stack>

              {activationMode === 'OTAA' ? (
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    required
                    label="AppKey"
                    value={appKey}
                    onChange={(event) => setAppKey(event.target.value)}
                    helperText="32 hex characters"
                    fullWidth
                  />
                  <TextField
                    label="NwkKey (optional)"
                    value={nwkKey}
                    onChange={(event) => setNwkKey(event.target.value)}
                    helperText="32 hex characters"
                    fullWidth
                  />
                </Stack>
              ) : (
                <>
                  <TextField
                    required
                    label="DevAddr"
                    value={devAddr}
                    onChange={(event) => setDevAddr(event.target.value)}
                    helperText="8 hex characters"
                  />
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <TextField
                      required
                      label="AppSKey"
                      value={appSKey}
                      onChange={(event) => setAppSKey(event.target.value)}
                      helperText="32 hex characters"
                      fullWidth
                    />
                    <TextField
                      required
                      label="NwkSKey"
                      value={nwkSKey}
                      onChange={(event) => setNwkSKey(event.target.value)}
                      helperText="32 hex characters"
                      fullWidth
                    />
                  </Stack>
                </>
              )}
            </>
          ) : (
            <Stack spacing={2}>
              <Typography variant="body2">
                Upload a JSON array. Each entry should include a device ID and the per-device EUIs/keys.
                Field names are flexible — supported keys: <code>device_id</code>/<code>deviceID</code>/<code>Name</code>,
                <code> dev_eui</code>/<code>DevEUI</code> (or last comma segment of <code>UID</code>),
                <code> join_eui</code>/<code>JoinEUI</code>/<code>JoinID</code>, and
                <code> app_key</code>/<code>AppKey</code>.
              </Typography>

              <Stack direction="row" spacing={2} alignItems="center">
                <Button variant="outlined" component="label">
                  Choose JSON file
                  <input
                    type="file"
                    accept="application/json,.json"
                    hidden
                    onChange={handleFileChange}
                  />
                </Button>
                {bulkFileName ? (
                  <Typography variant="body2">{bulkFileName}</Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No file selected
                  </Typography>
                )}
              </Stack>

              {bulkParseError ? (
                <Alert severity="error">{bulkParseError}</Alert>
              ) : null}

              {parsedDevices.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Device ID</TableCell>
                        <TableCell>DevEUI</TableCell>
                        <TableCell>JoinEUI</TableCell>
                        <TableCell>AppKey</TableCell>
                        <TableCell>Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {parsedDevices.map((d, idx) => (
                        <TableRow key={`${d.device_id}-${idx}`}>
                          <TableCell>{d.device_id || '—'}</TableCell>
                          <TableCell>{d.dev_eui || '—'}</TableCell>
                          <TableCell>{d.join_eui || '—'}</TableCell>
                          <TableCell>{d.app_key ? `${d.app_key.slice(0, 6)}…${d.app_key.slice(-4)}` : '—'}</TableCell>
                          <TableCell>
                            {d.issues.length === 0 ? (
                              <Chip label="Ready" color="success" size="small" />
                            ) : (
                              <Chip label={d.issues.join('; ')} color="warning" size="small" />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : null}
            </Stack>
          )}

          {validationError ? <Alert severity="warning">{validationError}</Alert> : null}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button type="submit" variant="contained" disabled={isSubmitting}>
              {submitLabel}
            </Button>
          </Box>
        </Stack>

        {(bulkSuccesses.length > 0 || bulkFailures.length > 0) && mode === 'bulk' ? (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Bulk results</Typography>

            {bulkSuccesses.length > 0 ? (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Registered ({bulkSuccesses.length})
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Device ID</TableCell>
                        <TableCell>DevEUI</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {bulkSuccesses.map((s) => (
                        <TableRow key={s.device_id}>
                          <TableCell>{s.device_id}</TableCell>
                          <TableCell>{s.dev_eui}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ) : null}

            {bulkFailures.length > 0 ? (
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Failed ({bulkFailures.length})
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Device ID</TableCell>
                        <TableCell>DevEUI</TableCell>
                        <TableCell>Stage</TableCell>
                        <TableCell>Error</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {bulkFailures.map((f, idx) => (
                        <TableRow key={`${f.device_id}-${idx}`}>
                          <TableCell>{f.device_id}</TableCell>
                          <TableCell>{f.dev_eui || '—'}</TableCell>
                          <TableCell>{f.stage || '—'}</TableCell>
                          <TableCell>
                            {f.error}
                            {f.details?.message ? ` — ${f.details.message}` : ''}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ) : null}
          </Box>
        ) : null}
      </Paper>

      <Snackbar
        open={Boolean(successMessage)}
        autoHideDuration={5000}
        onClose={() => setSuccessMessage('')}
      >
        <Alert severity="success" onClose={() => setSuccessMessage('')} sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={Boolean(errorMessage)}
        autoHideDuration={7000}
        onClose={() => setErrorMessage('')}
      >
        <Alert severity="error" onClose={() => setErrorMessage('')} sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TtnSensorRegistrationGrid;
